// Trustline validation service
import { ethers } from 'ethers';
import { trustline, TrustlineInitOptions, TrustlineWeb3ValidateParams, TrustlineValidateResponse, ConfigurePolicyParams, ConfigurePolicyResult, FetchPolicyParams, FetchPolicyResult, FetchDefaultPolicyParams, FetchDefaultPolicyResult } from '@trustline.id/websdk';


class TrustlineService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  // NOTE: Currently only validateWithdraw is active
  // validateDeposit and validateRefund are commented out in components but kept for future use

  private initialize() {
    try {
      const initOptions: TrustlineInitOptions = {
        clientId: process.env.REACT_APP_TRUSTLINE_CLIENT_ID || 'YOUR_TRUSTLINE_CLIENT_ID',
        loginUri: process.env.REACT_APP_TRUSTLINE_LOGIN_URI || 'https://yourapp.com/auth/trustline/callback'
      };
      trustline.init(initOptions);
      this.isInitialized = true;
      console.log('Trustline SDK initialized');
      // Debug: Check if configurePolicy is available
      console.log('configurePolicy available:', typeof trustline.configurePolicy === 'function');
      console.log('Available trustline methods:', Object.keys(trustline));
    } catch (error) {
      console.error('Failed to initialize Trustline SDK:', error);
    }
  }

  async validateTransaction(data: TrustlineWeb3ValidateParams): Promise<TrustlineValidateResponse> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }

    try {
      const response = await trustline.validate(data);
      return response;
    } catch (error) {
      console.error('Trustline validation failed:', error);
      throw error;
    }
  }

  async validateDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    tokenAddress: string,
    amount: string,
    beneficiary: string
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: tokenAddress === '0x0000000000000000000000000000000000000000' ? amount : '0',
        data: {
          functionPrototype: 'depose(address,address,uint256)',
          args: [tokenAddress, beneficiary, amount]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionPrototype: 'withdraw(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Withdraw transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Withdraw transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Withdraw transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateRefund(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionPrototype: 'refund(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Refund transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Refund transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Refund transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async configurePolicy(
    params: ConfigurePolicyParams,
    eip712Signer: (domain: any, types: any, message: any) => Promise<string>
  ): Promise<ConfigurePolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.configurePolicy(params, eip712Signer);
      return response;
    } catch (error) {
      console.error('Trustline configurePolicy failed:', error);
      throw error;
    }
  }

  async configurePolicyForWithdraw(
    chainId: number,
    senderAddress: string, // The beneficiary who will withdraw
    contractAddress: string,
    depositId: number,
    allowedEmailDomains: string[],
    signer: ethers.Signer
  ): Promise<ConfigurePolicyResult> {
    try {
      // Get signer address
      const signerAddress = await signer.getAddress();
      
      // Create EIP-712 signer function
      const eip712Signer = async (domain: any, types: any, message: any) => {
        return await signer.signTypedData(domain, types, message);
      };

      const params: ConfigurePolicyParams = {
        chainId: chainId.toString(),
        senderAddress: senderAddress,
        signerAddress: signerAddress,
        contractAddress: contractAddress,
        nativeAmount: '0',
        data: {
          functionPrototype: 'withdraw(uint256)',
          args: [depositId.toString()]
        },
        policyType: 'UserAuthenticationPolicy',
        customization: {
          allowedEmailDomains: allowedEmailDomains.map(domain => domain.toLowerCase()),
          requireEmailVerification: true
        }
      };

      const response = await this.configurePolicy(params, eip712Signer);
      return response;
    } catch (error) {
      console.error('Trustline configurePolicyForWithdraw failed:', error);
      throw error;
    }
  }

  async fetchPolicy(
    params: FetchPolicyParams
  ): Promise<FetchPolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.fetchPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchPolicy failed:', error);
      throw error;
    }
  }

  async fetchPolicyForWithdraw(
    chainId: number,
    senderAddress: string, // The beneficiary who will withdraw
    contractAddress: string,
    depositId: number
  ): Promise<FetchPolicyResult> {
    try {
      const params: FetchPolicyParams = {
        chainId: chainId.toString(),
        senderAddress: senderAddress,
        contractAddress: contractAddress,
        nativeAmount: '0',
        data: {
          functionPrototype: 'withdraw(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.fetchPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchPolicyForWithdraw failed:', error);
      throw error;
    }
  }

  async fetchDefaultPolicy(
    params: FetchDefaultPolicyParams
  ): Promise<FetchDefaultPolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.fetchDefaultPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchDefaultPolicy failed:', error);
      throw error;
    }
  }

  async fetchDefaultPolicyForWithdraw(
    chainId: number,
    contractAddress: string
  ): Promise<FetchDefaultPolicyResult> {
    try {
      const params: FetchDefaultPolicyParams = {
        chainId: chainId.toString(),
        contractAddress: contractAddress,
        data: {
          functionPrototype: 'withdraw(uint256)',
          args: ['0'] // Use deposit #0 for default policy lookup
        }
      };

      const response = await this.fetchDefaultPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchDefaultPolicyForWithdraw failed:', error);
      throw error;
    }
  }
}

export const trustlineService = new TrustlineService();
